import React, { useState } from 'react';
import { sendPdfByEmail } from '../../api';

/**
 * Reusable "Email this PDF" button + modal.
 *
 * Usage:
 *
 *   <SendPdfButton
 *     authAxios={authAxios}
 *     showFormMessage={showFormMessage}
 *     fetchPdf={() => fetchInvoicePdf(authAxios, invoice.id)}
 *     defaultFilename={`INV-${invoice.invoice_number}.pdf`}
 *     defaultSubject={`Invoice ${invoice.invoice_number} from ${firmName}`}
 *     defaultBody={`Dear ${client.name},\n\nPlease find attached...`}
 *     defaultRecipients={[client.email]}
 *     documentKind="INVOICE"
 *     objectId={invoice.id}
 *   />
 *
 * Calls the supplied `fetchPdf` (must resolve to a Blob), base64-encodes it
 * client-side, and posts to `/send-pdf-by-email/` for the server to email
 * with the firm as the reply-to address.
 */
export default function SendPdfButton({
  authAxios,
  showFormMessage,
  fetchPdf,
  defaultFilename,
  defaultSubject = '',
  defaultBody = '',
  defaultRecipients = [],
  defaultCc = [],
  documentKind = '',
  objectId = '',
  caseId = null,
  clientId = null,
  buttonLabel = 'Email',
  buttonClassName = 'px-3 py-1.5 text-xs border border-gray-300 text-gray-700 bg-white rounded-md hover:bg-gray-50',
}) {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [recipients, setRecipients] = useState(
    Array.isArray(defaultRecipients) ? defaultRecipients.join(', ') : (defaultRecipients || '')
  );
  const [cc, setCc] = useState(Array.isArray(defaultCc) ? defaultCc.join(', ') : (defaultCc || ''));
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const pdf = await fetchPdf();
      const blob = pdf instanceof Blob ? pdf : new Blob([pdf], { type: 'application/pdf' });
      await sendPdfByEmail(authAxios, {
        pdf: blob,
        filename: defaultFilename || 'document.pdf',
        recipients: recipients.split(',').map(s => s.trim()).filter(Boolean),
        cc: cc.split(',').map(s => s.trim()).filter(Boolean),
        subject,
        body,
        document_kind: documentKind,
        object_id: objectId,
        case_id: caseId,
        client_id: clientId,
      });
      showFormMessage?.('success', 'Email sent.');
      setOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.detail ?? err?.message ?? 'Failed to send email.';
      showFormMessage?.('error', msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className={buttonClassName} title="Email this PDF">
        ✉ {buttonLabel}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="px-6 py-5 border-b flex items-center justify-between">
              <h3 className="font-semibold">Email this document</h3>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <form onSubmit={handleSend} className="p-6 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">To *</label>
                <input required value={recipients} onChange={e => setRecipients(e.target.value)}
                  placeholder="recipient@example.com, another@example.com"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Cc</label>
                <input value={cc} onChange={e => setCc(e.target.value)}
                  placeholder="cc1@example.com"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Subject *</label>
                <input required value={subject} onChange={e => setSubject(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Message *</label>
                <textarea required value={body} onChange={e => setBody(e.target.value)} rows={6}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <p className="text-xs text-gray-500">
                The PDF will be attached automatically. The firm's email address is the reply-to.
              </p>
              <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setOpen(false)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={sending}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60">
                  {sending ? 'Sending…' : 'Send email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
